import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Input cleaning utility
export function cleanInput(value: string): string {
  return value.trim()
}

// Clean all string values in an object
export function cleanFormData<T extends Record<string, any>>(data: T): T {
  const cleaned = { ...data } as any
  
  Object.keys(cleaned).forEach(key => {
    if (typeof cleaned[key] === 'string') {
      cleaned[key] = cleanInput(cleaned[key])
    }
  })
  
  return cleaned
}

// Error message mapping for consistent user messaging
export const ErrorMessages = {
  // Authentication errors
  INVALID_CREDENTIALS: "Please login with valid email and password.",
  SESSION_EXPIRED: "Your session has expired. Please log in again.",
  ACCESS_DENIED: "You don't have permission to perform this action.",
  
  // Validation errors
  REQUIRED_FIELD: "This field is required.",
  INVALID_EMAIL: "Please enter a valid email address.",
  INVALID_PASSWORD: "Password must be at least 8 characters long.",
  INVALID_PHONE: "Please enter a valid phone number.",
  INVALID_DATE: "Please enter a valid date.",
  
  // Network errors
  NETWORK_ERROR: "Network error. Please check your connection and try again.",
  SERVER_ERROR: "Server error. Please try again later.",
  
  // Generic errors
  UNKNOWN_ERROR: "Something went wrong. Please try again.",
  SAVE_ERROR: "Failed to save changes. Please try again.",
  DELETE_ERROR: "Failed to delete item. Please try again.",
  UPDATE_ERROR: "Failed to update item. Please try again.",
  LOAD_ERROR: "Failed to load data. Please try again.",
}

// Error handler utility
export function getErrorMessage(error: any): string {
  // Handle API response errors
  if (error?.response?.status) {
    switch (error.response.status) {
      case 400:
        return ErrorMessages.INVALID_CREDENTIALS
      case 401:
        return ErrorMessages.SESSION_EXPIRED
      case 403:
        return ErrorMessages.ACCESS_DENIED
      case 404:
        return "The requested resource was not found."
      case 422:
        return "Invalid data provided. Please check your input."
      case 500:
        return ErrorMessages.SERVER_ERROR
      default:
        return ErrorMessages.UNKNOWN_ERROR
    }
  }
  
  // Handle specific error messages
  if (error?.message) {
    const message = error.message.toLowerCase()
    
    if (message.includes('invalid credentials') || message.includes('unauthorized')) {
      return ErrorMessages.INVALID_CREDENTIALS
    }
    if (message.includes('expired') || message.includes('token')) {
      return ErrorMessages.SESSION_EXPIRED
    }
    if (message.includes('forbidden') || message.includes('permission')) {
      return ErrorMessages.ACCESS_DENIED
    }
    if (message.includes('network') || message.includes('fetch')) {
      return ErrorMessages.NETWORK_ERROR
    }
    if (message.includes('email')) {
      return ErrorMessages.INVALID_EMAIL
    }
    if (message.includes('password')) {
      return ErrorMessages.INVALID_PASSWORD
    }
  }
  
  // Return generic error message for unknown errors
  return ErrorMessages.UNKNOWN_ERROR
}
